import React from 'react';
import { useGetProcurementDemandItemsQuery } from '../../store/endpoints/ProcurementEndpoints';
import { GridSelectionMode, IColumnDetails } from '../../models/GridConfiguration';
import { IProcurementDemandItem } from '../../models/Procurement';
import { ICommandBarItemProps, IconButton } from '@fluentui/react';
import GridComposer from '../../components/GridComposer/GridComposer';
import ReSourcePanel from '../../components/Procurement/ReSourcePanel/ReSourcePanel';
import ProcureDemandPanel from '../../components/Procurement/ProcureDemand/ProcureDemandPanel';
import ProcureDemandMultiPanel from '../../components/Procurement/ProcureDemand/ProcureDemandMultiPanel';
import ExistingOrderPanel from '../../components/Procurement/Order/ExistingOrderPanel';

// Helper to parse query string into filters object
const getFiltersFromQueryString = () => {
    const params = new URLSearchParams(window.location.search);
    const filtersObj: { [key: string]: string } = {};
    for (const [key, value] of params.entries()) {
        if (value !== undefined && value !== null) {
            filtersObj[key] = value;
        }
    }
    return filtersObj;
};

// Helper to update query string from filters object
const setQueryStringFromFilters = (filtersObj: { [key: string]: string }) => {
    const params = new URLSearchParams();
    Object.entries(filtersObj).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            params.set(key, value);
        }
    });
    const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    window.history.replaceState({}, '', newUrl);
};

const ProcurementDemandItemsPage: React.FC = () => {
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(50);
    const [sortBy, setSortBy] = React.useState<string>('lastUpdated');
    const [sortDirection, setSortDirection] = React.useState<string>('desc');
    const [selectedDemandItemId, setSelectedDemandItemId] = React.useState<string | undefined>(undefined);
    const [filters, setFilters] = React.useState<{ [key: string]: string }>(() => getFiltersFromQueryString());
    const [selectedDemands, setSelectedDemands] = React.useState<string[]>([]);
    const [reSourceOpen, setReSourceOpen] = React.useState(false);
    const [canOrder, setCanOrder] = React.useState(false);
    const [orderOpen, setOrderOpen] = React.useState(false);
    const [createdOrderId, setCreatedOrderId] = React.useState<string | undefined>(undefined);


    // Update query string when filters change
    React.useEffect(() => {
        setQueryStringFromFilters(filters);
    }, [filters]);

    // On mount, load filters from query string (in case of browser navigation)
    React.useEffect(() => {
        setFilters(getFiltersFromQueryString());
        // Listen for popstate (back/forward navigation)
        const onPopState = () => {
            setFilters(getFiltersFromQueryString());
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, []);

    const items = useGetProcurementDemandItemsQuery({
        conditions: {
            page: page,
            pageSize: pageSize,
            sortBy: sortBy,
            sortDirection: sortDirection,
            filters: filters,
        }
    })

    React.useEffect(() => {
        if (selectedDemands.length === 0) {
            setCanOrder(false);
        } else {
            // Check if all demands are for the same vendor
            const demandItems = items.data?.items?.filter(item => selectedDemands.includes(item.id)) || [];
            const uniqueVendors = new Set(demandItems.map(item => item.vendor));
            setCanOrder(uniqueVendors.size === 1);
        }
    }, [selectedDemands, items.data?.items]);

    const handleProcured = (orderId: string) => {
        setSelectedDemandItemId(undefined);
        setOrderOpen(false);
        setSelectedDemands([]);
        setCreatedOrderId(orderId);
        items.refetch();
    }

    const selectedDemandItems = items.data?.items?.filter(item => selectedDemands.includes(item.id)) || [];

    const getCustomColumns = (): IColumnDetails[] => ([
        {
            identifier: 'Details',
            label: '',
            type: '',
            component: (item: IProcurementDemandItem) => <IconButton
                iconProps={{ iconName: 'EntryView' }}
                onClick={() => setSelectedDemandItemId(item.id)}
                />,
            required: true
        },
    ])

    const additionalCommands: ICommandBarItemProps[] = [
        {
            key: 'resource',
            text: 'Re-Source Selected',
            iconProps: { iconName: 'DeliveryTruck' },
            disabled: selectedDemands.length === 0,
            onClick: () => setReSourceOpen(true),
        },
        {
            key: 'order',
            text: 'Create Order',
            iconProps: { iconName: 'ShoppingCart' },
            disabled: !canOrder,
            onClick: () => setOrderOpen(true),
        }
    ]

    return (
        <div className='main-content'>
            <h1>Procurement Demand Items</h1>
            <GridComposer
                columnSetName='ProcurementDemandItem'
                initialPageSize={50}
                data={items.data ?? {}}
                loading={items.isLoading || items.isFetching}
                onRefresh={() => items.refetch()}
                page={page}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                selectionMode={GridSelectionMode.multiple}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={(newSortBy, newSortDirection) => {
                    setSortBy(newSortBy ?? 'lastUpdated');
                    setSortDirection(newSortDirection ?? 'desc');
                }}
                canChangePageSize={false}
                canCreateCsv={false}
                canCustomizeColumns
                canResizeColumns
                canDragColumns
                overrideColumns={getCustomColumns()}
                filters={filters}
                onFiltersChange={setFilters}
                showFiltersAsTags
                additionalCommands={additionalCommands}
                selectedItems={selectedDemands}
                onSelectedItemsChange={setSelectedDemands}
                getItemId={(item: IProcurementDemandItem) => item.id}
                />
                {reSourceOpen && <ReSourcePanel
                    demandIds={selectedDemands}
                    onClose={() => setReSourceOpen(false)}
                    onReSourceComplete={() => {
                        setReSourceOpen(false)
                        items.refetch()
                    }}
                    />
                }
                {selectedDemandItemId && <ProcureDemandPanel
                    demandId={selectedDemandItemId}
                    onDismiss={() => setSelectedDemandItemId(undefined)}
                    onProcure={handleProcured}
                    />
                }
                {orderOpen && selectedDemandItems.length > 0 && <ProcureDemandMultiPanel
                    demands={selectedDemandItems}
                    onDismiss={() => setOrderOpen(false)}
                    onProcure={handleProcured}
                    />
                }
                <ExistingOrderPanel
                    orderId={createdOrderId}
                    onClose={() => setCreatedOrderId(undefined)}
                    />

        </div>
    );
}

export default ProcurementDemandItemsPage;